import { TablePager, useSeiten } from '../components/TablePager';
import { apiHref } from '../lib/apiHref';
import { useNavigate } from '@tanstack/react-router';
import { Button, Checkbox, Select, Table, Text } from '@mantine/core';
import { usePaymentsControllerOpenItems, useCustomersControllerList } from '@werkbank/shared';
import { useGemerkt } from '../lib/gemerkt';
// Vollanalyse S1: Mahnstufen-Labels aus der kanonischen Quelle — die frühere
// lokale Kurzform „Erinnerung" wich vom Mahnwesen ab („Zahlungserinnerung").
import {
  DUNNING_LEVEL_LABELS,
  formatCents,
  invoiceKindSuffix,
  formatCount,
  formatDateDE,
} from '../lib/format';
import { ListPage } from '../components/ListPage';
import { listState } from '../components/listPageContract';
import { TableCard } from '../components/TableCard';
import { EmptyState } from '../components/EmptyState';
import { DateRangeFilter } from '../components/DateRangeFilter';

/** Offene-Posten-Liste (4.7): Filter, Summenzeile, CSV-Export. */
export function OpenItemsPage() {
  const navigate = useNavigate();
  // Tobias 2026-09-25: Filter bleiben beim Wechsel der Seite erhalten.
  const [customerId, setCustomerId] = useGemerkt<string | null>('op.kunde', null);
  const [from, setFrom] = useGemerkt('op.von', '');
  const [to, setTo] = useGemerkt('op.bis', '');
  const [overdueOnly, setOverdueOnly] = useGemerkt('op.ueberfaellig', false);

  const customers = useCustomersControllerList({ take: 1000 });
  const query = {
    customerId: customerId ?? undefined,
    from: from || undefined,
    to: to || undefined,
    overdueOnly: overdueOnly ? ('true' as const) : undefined,
  };
  const op = usePaymentsControllerOpenItems(query);

  const csvUrl = apiHref('/api/open-items/csv', query);

  const customerName = customerId
    ? customers.data?.find((c) => c.id === customerId)?.displayName
    : null;
  const meta = op.data
    ? formatCount(op.data.items.length, 'offene Rechnung', 'offene Rechnungen') +
      (overdueOnly ? ' · nur überfällige' : '') +
      (customerName ? ` · ${customerName}` : '') +
      (from || to
        ? ` · Belegdatum ${formatDateDE(from) || '…'} bis ${formatDateDE(to) || '…'}`
        : '')
    : 'lädt…';

  // D-W1 Welle 2 (A2-13, W2-B4): Seite auf das ListPage-Gerüst migriert —
  // die vier Zustände (Laden/Fehler/Leer/Gefüllt) trägt das Gerüst.
  const seiten = useSeiten(op.data?.items, 'offene-posten');
  return (
    <ListPage
      title="Offene Posten"
      meta={meta}
      action={
        <Button component="a" href={csvUrl} variant="default" data-testid="op-csv">
          CSV-Export
        </Button>
      }
      filters={
        <>
          <Select
            placeholder="Kunde"
            clearable
            searchable
            data={(customers.data ?? []).map((c) => ({ value: c.id, label: c.displayName }))}
            value={customerId}
            onChange={setCustomerId}
            w={260}
            data-testid="op-filter-customer"
          />
          <DateRangeFilter from={from} to={to} setFrom={setFrom} setTo={setTo} />
          <Checkbox
            label="nur überfällige"
            checked={overdueOnly}
            onChange={(e) => setOverdueOnly(e.currentTarget.checked)}
            data-testid="op-filter-overdue"
          />
        </>
      }
      state={listState(op, op.data?.items.length === 0)}
      errorText="Offene Posten konnten nicht geladen werden."
      empty={
        <TableCard
          testId="op-card"
          footer={<TablePager p={seiten} einzahl="Posten" mehrzahl="Posten" testId="op-pager" />}
        >
          <EmptyState text="Keine offenen Posten. Alles bezahlt." />
        </TableCard>
      }
    >
      <TableCard testId="op-card">
        <Table highlightOnHover data-testid="op-table">
          <Table.Thead>
            <Table.Tr>
              {/* D-W1 Welle 3 (OP-1-Rest): „Tage überfällig" rückt direkt hinter
                  die Fälligkeit (Datum → Verzug → Konsequenz Mahnstufe), danach
                  der Betragsblock in Rechenreihenfolge Brutto − Bezahlt − Skonto
                  = Offen. Selten gefüllte Spalten (Bezahlt/Skonto) bleiben —
                  Nullwerte werden als gedimmtes „—" beruhigt. */}
              <Table.Th>Nummer</Table.Th>
              <Table.Th>Kunde</Table.Th>
              <Table.Th>Belegdatum</Table.Th>
              <Table.Th>Fälligkeit</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Tage überfällig</Table.Th>
              <Table.Th>Mahnstufe</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Brutto</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Bezahlt</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Skonto</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Offen</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {seiten.sichtbar.map((i) => (
              <Table.Tr
                key={i.documentId}
                style={{ cursor: 'pointer' }}
                onClick={() =>
                  void navigate({
                    to: '/documents/$documentId',
                    params: { documentId: i.documentId },
                  })
                }
              >
                <Table.Td className="wb-tnum">
                  {i.number}
                  {invoiceKindSuffix(i.invoiceKind)}
                  {i.importedLegacy ? ' (Sage)' : ''}
                </Table.Td>
                <Table.Td>{i.customerName}</Table.Td>
                <Table.Td className="wb-tnum">{formatDateDE(i.issueDate)}</Table.Td>
                <Table.Td className="wb-tnum">{formatDateDE(i.dueDate)}</Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  {i.daysOverdue > 0 ? (
                    <Text span c="var(--wb-status-overdue)" fw={600} className="wb-tnum">
                      {i.daysOverdue}
                    </Text>
                  ) : (
                    '0'
                  )}
                </Table.Td>
                <Table.Td>
                  {i.dunningLevel ? (
                    <Text span size="sm" c="var(--wb-status-overdue)" fw={600}>
                      {DUNNING_LEVEL_LABELS[i.dunningLevel]}
                      {i.dunningFeeCents > 0 ? ` · +${formatCents(i.dunningFeeCents)} €` : ''}
                    </Text>
                  ) : (
                    <Text span c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  {formatCents(i.grossTotalCents)} €
                </Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  {i.paidCents === 0 ? (
                    <Text span c="dimmed">
                      —
                    </Text>
                  ) : (
                    `${formatCents(i.paidCents)} €`
                  )}
                </Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  {i.skontoCents === 0 ? (
                    <Text span c="dimmed">
                      —
                    </Text>
                  ) : (
                    `${formatCents(i.skontoCents)} €`
                  )}
                </Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  <Text span fw={600} className="wb-tnum">
                    {formatCents(i.openCents)} €
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
            {op.data && op.data.items.length > 0 && (
              <Table.Tr data-testid="op-sum-row">
                {/* D-W1 OP-1: Summen-Label rechtsbündig direkt vor der ersten
                    Summenspalte statt verloren am linken Tabellenrand. */}
                <Table.Td colSpan={6}>
                  <Text fw={700} ta="right">
                    Summe
                  </Text>
                </Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  <Text fw={700} className="wb-tnum">
                    {formatCents(op.data.totals.payableCents)} €
                  </Text>
                </Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  <Text
                    span
                    c={op.data.totals.paidCents === 0 ? 'dimmed' : undefined}
                    className="wb-tnum"
                  >
                    {formatCents(op.data.totals.paidCents)} €
                  </Text>
                </Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  <Text
                    span
                    c={op.data.totals.skontoCents === 0 ? 'dimmed' : undefined}
                    className="wb-tnum"
                  >
                    {formatCents(op.data.totals.skontoCents)} €
                  </Text>
                </Table.Td>
                <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                  <Text fw={700} className="wb-tnum">
                    {formatCents(op.data.totals.openCents)} €
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </TableCard>
    </ListPage>
  );
}
